'use client';
import { BrandDocument } from '@models/brand';
import Input from '@ui/Input';
import { PaginatorClient } from '@ui/Paginator';
import SafeImage from '@ui/SafeImage';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { useState } from 'react';

const alpha = Array.from(Array(26)).map((e, i) => i + 65);
const alphabet = alpha.map((x) => String.fromCharCode(x));

export type BrandProp = BrandDocument & { url: string };
type BrandsPageProps = {
  brands: BrandProp[];
};

function getFilteredBrands({
  brands,
  search,
  startsWith,
}: {
  brands: BrandProp[];
  search: string;
  category?: string;
  startsWith: string;
}) {
  if (startsWith && startsWith !== 'ALL') {
    if (startsWith === '#') {
      brands = brands.filter((b) => !!b.name.match(/^\d/g));
    } else {
      brands = brands.filter((b) =>
        b.name.toLowerCase().startsWith(startsWith.toLowerCase())
      );
    }
  }
  if (search) {
    brands = brands.filter((b) =>
      b.name.toLowerCase().includes(search.toLowerCase())
    );
  }
  return brands;
}

const Brands = (props: BrandsPageProps) => {
  // if (typeof props === 'undefined') {
  //   logError('BrandsPageProps is undefined');
  // }
  const { brands } = props;
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    sort: '',
    charFilter: '',
  });
  const [page, setPage] = useState(1);
  const currentBrands = getFilteredBrands({
    brands,
    search,
    startsWith: filters.charFilter,
  });
  const pageSize = 15;
  const totalPages = Math.ceil(currentBrands.length / pageSize);
  const start = (page - 1) * pageSize;
  const end = Math.min(start + pageSize, currentBrands.length);

  const handleSelectChange = (value: string, name: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const charFilters = ['ALL', ...alphabet, '#'];

  return (
    <div className="mx-auto flex w-full max-w-default flex-col gap-[2.4rem] p-4 sm:gap-[4rem] sm:px-default-padding-x">
      <h1 className="mt-[6rem] text-[4.4rem] font-semibold">Brands</h1>
      {/* search row */}
      <div className="flex flex-col gap-[1.6rem] sm:flex-row">
        <div className="w-full grow sm:max-w-[46rem]">
          <Input
            type="search"
            placeholder="Find your favorite brands..."
            onChange={(e) => setSearch(e.target.value)}
            full
          />
        </div>
      </div>
      {/* char filter row */}
      <div className="flex w-full justify-between overflow-x-scroll">
        {charFilters.map((c) => {
          const selected = c === filters.charFilter;
          return (
            <button
              className={`mx-2 font-semibold transition-colors hover:text-brand-primary ${
                selected ? 'text-brand-secondary' : 'text-brand-dark-gray'
              }`}
              key={c}
              onClick={() => handleSelectChange(c, 'charFilter')}
            >
              {c.toUpperCase()}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-[2.4rem]">
        {currentBrands.slice(start, end).map((b) => {
          return (
            <Link
              href={'/brands/' + b.url}
              key={b.url}
              className={cx([
                'relative flex h-[18rem] min-w-[15rem] grow items-center justify-center rounded-[1.6rem] border-2 border-brand-lightest-gray sm:min-w-[22rem] sm:max-w-[22rem]',
                'opacity-70 grayscale transition-all duration-150 hover:opacity-100 hover:grayscale-0',
              ])}
            >
              {b.image?.startsWith('http') ? (
                <SafeImage
                  src={b.image}
                  alt={b.name}
                  key={b.image}
                  className="object-contain"
                  fill
                />
              ) : (
                <h1 className="text-center text-[2.4rem] font-semibold">
                  {b.name}
                </h1>
              )}
            </Link>
          );
        })}
        {/* render hidden links for SEO of other brands */}
        {brands.map((b) => {
          return (
            <Link href={'/brands/' + b.url} key={b.url} hidden>
              {b.name}
            </Link>
          );
        })}
      </div>

      <PaginatorClient
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(n) => setPage(n)}
      />
    </div>
  );
};

export default Brands;
